import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  ChevronLeft, ShieldCheck, Upload, FileText, CheckCircle2, XCircle, Clock,
  RefreshCw, Ban, Eye, EyeOff, Loader2, AlertTriangle, Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const CRED_TYPES = [
  { value: "food_handler", label: "Food Handler" },
  { value: "food_manager", label: "Food Manager (ServSafe)" },
  { value: "business_license", label: "Business License" },
  { value: "sellers_permit", label: "Seller's Permit" },
  { value: "health_permit", label: "Health Permit" },
  { value: "cottage_food", label: "Cottage Food" },
  { value: "professional_cert", label: "Professional Cert." },
  { value: "other", label: "Other" },
];

const STATUS_META = {
  pending_review: { label: "Pending review", tone: "amber", Icon: Clock, help: "We're reviewing your document. This usually takes 1–2 business days." },
  verified:       { label: "Verified", tone: "emerald", Icon: CheckCircle2, help: "Your credential is Hidden Gem Verified." },
  rejected:       { label: "Rejected", tone: "rose", Icon: XCircle, help: null },
  needs_update:   { label: "Needs update", tone: "cyan", Icon: RefreshCw, help: null },
  expired:        { label: "Expired", tone: "zinc", Icon: Clock, help: "This credential's expiration date has passed. Renew to keep the Verified badge." },
  revoked:        { label: "Revoked", tone: "red", Icon: Ban, help: null },
};

function StatusPill({ status }) {
  const M = STATUS_META[status] || STATUS_META.pending_review;
  const Icon = M.Icon;
  const toneCls = {
    amber: "bg-amber-500/15 border-amber-400/40 text-amber-100",
    emerald: "bg-emerald-500/15 border-emerald-400/40 text-emerald-100",
    rose: "bg-rose-500/15 border-rose-400/40 text-rose-100",
    cyan: "bg-cyan-500/15 border-cyan-400/40 text-cyan-100",
    zinc: "bg-zinc-500/15 border-zinc-400/40 text-zinc-100",
    red: "bg-red-500/15 border-red-400/40 text-red-100",
  }[M.tone];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${toneCls}`} data-testid={`credential-status-${status}`}>
      <Icon className="w-3 h-3" /> {M.label}
    </span>
  );
}

export default function VendorCredentials() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const businessId = useMemo(() => (user?.user_id ? `biz_${user.user_id}` : null), [user]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewCred, setPreviewCred] = useState(null);
  const [previewMime, setPreviewMime] = useState("");
  const [busyCredId, setBusyCredId] = useState(null);

  // Form state (also used for replace/renew — if replacingId is set, we replace)
  const [form, setForm] = useState({
    type: "food_handler",
    custom_type_label: "",
    issuing_organization: "",
    issue_date: "",
    expires_at: "",
    credential_number: "",
    state_jurisdiction: "",
  });
  const [file, setFile] = useState(null);
  const [replacingId, setReplacingId] = useState(null);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/businesses/${businessId}/credentials`);
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(e.response?.data?.detail || "Couldn't load credentials");
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  if (user && !user.is_vendor) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-3" data-testid="credentials-not-vendor">
        <Lock className="w-10 h-10 text-zinc-500 mx-auto" />
        <h1 className="text-3xl font-black tracking-tighter">Vendors only</h1>
        <p className="text-sm text-zinc-400">Open a shop first to submit credentials.</p>
        <Button onClick={() => navigate("/sell")} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-11 px-5">Open Sell</Button>
      </div>
    );
  }

  const resetForm = () => {
    setForm({ type: "food_handler", custom_type_label: "", issuing_organization: "", issue_date: "", expires_at: "", credential_number: "", state_jurisdiction: "" });
    setFile(null);
    setReplacingId(null);
  };

  const submit = async () => {
    if (!file) return toast.error("Attach the credential document (PNG, JPEG, WebP, or PDF).");
    if (!form.type) return toast.error("Pick a credential type.");
    if (form.type === "other" && !form.custom_type_label.trim()) return toast.error("Add a label for the custom credential type.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("business_id", businessId);
      fd.append("type", form.type);
      if (form.custom_type_label) fd.append("custom_type_label", form.custom_type_label);
      if (form.issuing_organization) fd.append("issuing_organization", form.issuing_organization);
      if (form.issue_date) fd.append("issue_date", form.issue_date);
      if (form.expires_at) fd.append("expires_at", form.expires_at);
      if (form.credential_number) fd.append("credential_number", form.credential_number);
      if (form.state_jurisdiction) fd.append("state_jurisdiction", form.state_jurisdiction);
      fd.append("file", file);
      await api.post("/credentials/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(replacingId ? "Replacement submitted — re-review pending." : "Credential submitted for review.");
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setUploading(false);
    }
  };

  const startReplace = (c) => {
    setReplacingId(c.credential_id);
    setForm({
      type: c.type,
      custom_type_label: c.custom_type_label || "",
      issuing_organization: c.issuing_organization || "",
      issue_date: c.issue_date || "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
      credential_number: "",
      state_jurisdiction: c.state_jurisdiction || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Fill in the fields and attach the updated document below.");
  };

  const togglePublic = async (c, next) => {
    setBusyCredId(c.credential_id);
    try {
      await api.patch(`/credentials/${c.credential_id}/public-display`, { public_display: next });
      toast.success(next ? "Now shown on your public storefront." : "Hidden from your public storefront.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update visibility");
    } finally {
      setBusyCredId(null);
    }
  };

  const viewFile = async (c) => {
    setPreviewCred(c);
    setPreviewUrl(null);
    setPreviewMime(c.file_mime || "");
    try {
      const { data } = await api.post(`/credentials/${c.credential_id}/file-signed-url`);
      setPreviewUrl(`${process.env.REACT_APP_BACKEND_URL}${data.signed_url}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open document");
      setPreviewCred(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 space-y-4" data-testid="vendor-credentials-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white" data-testid="credentials-back">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="rounded-3xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 via-zinc-950 to-cyan-500/10 p-5">
        <p className="inline-flex items-center gap-2 text-emerald-300 text-[10px] uppercase tracking-[0.3em] font-black">
          <ShieldCheck className="w-3 h-3" /> Vendor Trust
        </p>
        <h1 className="text-3xl font-black tracking-tighter mt-1">Credentials & Verification</h1>
        <p className="text-sm text-zinc-400 mt-1">Submit food-handler and business permits for verification. Your document stays private — customers only see that you're Hidden Gem Verified.</p>
      </header>

      {/* Submission form */}
      <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5 space-y-3" data-testid="credentials-submit-card">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black tracking-tight">
            {replacingId ? "Replace / renew document" : "Submit a new credential"}
          </h2>
          {replacingId && (
            <button onClick={resetForm} className="text-xs text-zinc-400 hover:text-white" data-testid="credentials-cancel-replace">Cancel replacement</button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Credential type</Label>
            <select
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="h-11 w-full bg-white/5 border border-white/10 rounded-2xl text-white px-3"
              data-testid="credentials-type-select"
            >
              {CRED_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {form.type === "other" && (
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Custom label</Label>
              <Input value={form.custom_type_label} onChange={(e) => setForm({ ...form, custom_type_label: e.target.value })} placeholder="e.g. Kosher supervision" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="credentials-custom-label" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Issuing organization</Label>
            <Input value={form.issuing_organization} onChange={(e) => setForm({ ...form, issuing_organization: e.target.value })} placeholder="City Health Dept, ServSafe…" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="credentials-issuer" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Credential number</Label>
            <Input value={form.credential_number} onChange={(e) => setForm({ ...form, credential_number: e.target.value })} placeholder="Optional" className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="credentials-number" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Issue date</Label>
            <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="credentials-issue-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Expiration date</Label>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="credentials-expires" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">State / jurisdiction</Label>
            <Input value={form.state_jurisdiction} onChange={(e) => setForm({ ...form, state_jurisdiction: e.target.value })} placeholder="CA, NYC, etc." className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="credentials-state" />
          </div>
        </div>

        <div>
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Document (PNG · JPG · WebP · PDF · max 8 MB)</Label>
          <label className="mt-1.5 flex items-center gap-3 rounded-2xl border-2 border-dashed border-white/15 hover:border-emerald-400 cursor-pointer p-4 bg-white/[0.02]" data-testid="credentials-file-drop">
            <Upload className="w-5 h-5 text-emerald-300" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white">{file ? file.name : "Attach a PDF or clear photo of the credential"}</p>
              <p className="text-[11px] text-zinc-400 mt-0.5">Stored privately — only Hidden Gem admins and you can view it.</p>
            </div>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="credentials-file-input"
            />
          </label>
        </div>

        <Button
          onClick={submit}
          disabled={uploading}
          className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold"
          data-testid="credentials-submit-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
          {replacingId ? "Submit replacement for review" : "Submit for verification"}
        </Button>
      </section>

      {/* List */}
      <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="credentials-list-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-black tracking-tight">Your credentials</h2>
          <button onClick={load} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="credentials-refresh">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        {loading ? (
          <div className="py-6 text-center text-zinc-500 inline-flex items-center justify-center gap-2 w-full text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <p className="py-6 text-center text-zinc-500 text-sm" data-testid="credentials-empty">No credentials submitted yet.</p>
        ) : (
          <div className="space-y-2">
            {items.map((c) => {
              const M = STATUS_META[c.status] || STATUS_META.pending_review;
              const label = CRED_TYPES.find((t) => t.value === c.type)?.label || c.type;
              return (
                <div key={c.credential_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={`credential-row-${c.credential_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl grid place-items-center bg-emerald-500/15 text-emerald-300 shrink-0">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-black tracking-tight">{label}{c.custom_type_label ? ` · ${c.custom_type_label}` : ""}</p>
                        <StatusPill status={c.status} />
                      </div>
                      <p className="text-[11px] text-zinc-400 mt-0.5">
                        {c.issuing_organization && <>Issuer: {c.issuing_organization}. </>}
                        {c.expires_at && <>Expires {new Date(c.expires_at).toLocaleDateString()}. </>}
                        Submitted {new Date(c.created_at).toLocaleDateString()}.
                      </p>
                      {c.reason && ["rejected", "needs_update", "revoked"].includes(c.status) && (
                        <div className="mt-2 rounded-xl bg-rose-500/10 border border-rose-400/30 px-3 py-2 text-[12px] text-rose-100" data-testid={`credential-reason-${c.credential_id}`}>
                          <span className="font-black uppercase tracking-widest text-[10px] mr-1">Admin note:</span>{c.reason}
                        </div>
                      )}
                      {M.help && !c.reason && (
                        <p className="text-[11px] text-zinc-400 mt-1.5">{M.help}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => viewFile(c)}
                      className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold inline-flex items-center gap-1.5"
                      data-testid={`credential-view-${c.credential_id}`}
                    >
                      <Eye className="w-3.5 h-3.5" /> View document
                    </button>
                    <button
                      onClick={() => startReplace(c)}
                      className="h-8 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold inline-flex items-center gap-1.5"
                      data-testid={`credential-replace-${c.credential_id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Replace / renew
                    </button>
                    {c.status === "verified" && (
                      <button
                        onClick={() => togglePublic(c, !c.public_display)}
                        disabled={busyCredId === c.credential_id}
                        className={`h-8 px-3 rounded-full border text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60 ${
                          c.public_display
                            ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
                            : "bg-white/5 border-white/10 text-zinc-300"
                        }`}
                        data-testid={`credential-toggle-public-${c.credential_id}`}
                      >
                        {c.public_display ? <><Eye className="w-3.5 h-3.5" /> Public metadata on</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden from public</>}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-[10px] text-zinc-500 mt-4">
          Private documents are protected by short-lived signed URLs and are never exposed on your public storefront — even when you toggle the badge on, customers only see the credential type + Hidden Gem Verified indicator.
        </p>
      </section>

      {/* Preview modal */}
      {previewCred && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/80 backdrop-blur-sm p-4" onClick={() => { setPreviewCred(null); setPreviewUrl(null); }} data-testid="credentials-preview-modal">
          <div className="w-full max-w-2xl bg-zinc-950 rounded-3xl border border-emerald-400/40 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <p className="text-sm font-black">{CRED_TYPES.find((t) => t.value === previewCred.type)?.label || previewCred.type}</p>
              <button onClick={() => { setPreviewCred(null); setPreviewUrl(null); }} className="w-8 h-8 rounded-full grid place-items-center bg-white/5 hover:bg-white/10" data-testid="credentials-preview-close">
                <XCircle className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4">
              {previewUrl ? (
                previewMime === "application/pdf" ? (
                  <object data={previewUrl} type="application/pdf" width="100%" height="500px" data-testid="credentials-preview-pdf">
                    <a className="text-pink-300 underline" href={previewUrl} target="_blank" rel="noreferrer">Open PDF in a new tab</a>
                  </object>
                ) : (
                  <img src={previewUrl} alt="credential" className="w-full rounded-2xl" data-testid="credentials-preview-image" />
                )
              ) : (
                <div className="py-10 text-center text-zinc-500 inline-flex items-center gap-2 justify-center w-full">
                  <Loader2 className="w-4 h-4 animate-spin" /> Fetching secure document…
                </div>
              )}
              <p className="text-[10px] text-zinc-500 mt-3">Signed URL expires in 5 minutes. Reload the page to fetch again.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
